import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { PlusOutlined, DownOutlined } from '@ant-design/icons';
import CreateEditReleaseModal from './CreateEditReleaseModal';
import { ReleaseStatus, ReleaseTimelinePhase } from '../types';
import { NewReleasePayloadType, tryNewReleaseAction } from '../actions/release';
import styles from './styles/timelineStyles';
import { AppComponentProps } from '../typescriptHelpers';
import { isNil } from 'lodash';
import ReleaseGroup from './ReleaseGroup';
import Dropdown from 'antd/lib/dropdown';
import Menu from 'antd/lib/menu';
import {
    selectOrg,
    selectApp,
    selectPaginatedReleasesLimitAndOffset,
    selectIsAppsDemoModeEnabled,
    selectIsWorkflowsSSRStepsEnabled,
} from '../selectors/selectors';
import { ReleasesRequest, tryFetchPaginatedEntities } from '../actions/entities';
import { FetchablePaginatedEntityType } from '../entityType';
import Button from 'antd/lib/button';
import PrepareHotfixOrRollbackReleaseModal from './rollbacks/PrepareHotfixOrRollbackReleaseModal';
import { PrepareNewWorkflowRunModal } from './prepareRelease/PrepareNewWorkflowRunModal';
import { selectCompletedReleases } from '@/selectors/memoizedSelectors';

enum PrepareReleaseType {
    Standard = 'release',
    Hotfix = 'hotfix',
}

interface State {
    createEditReleaseModalVisible: boolean;
    cretateEditHotfixModalVisible: boolean;
}

class Releases extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            createEditReleaseModalVisible: false,
            cretateEditHotfixModalVisible: false,
        };
    }

    componentDidMount(): void {
        if (this.props.shouldFetchCompletedReleases) {
            this.fetchCompletedReleases();
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.appId !== this.props.appId && this.props.shouldFetchCompletedReleases) {
            this.fetchCompletedReleases();
        }
    }

    fetchCompletedReleases = () => {
        const limit = this.props.releasesPaginationLimit ?? 10;
        const offset = this.props.releasePaginationOffset ?? 0;

        this.props.fetchReleases(
            {
                orgId: this.props.orgId,
                appId: this.props.appId,
                orgIdParam: this.props.orgIdParam,
                appIdParam: this.props.appIdParam,
            },
            limit,
            offset,
        );
    };

    handleMenuClick = (e) => {
        if (e.key == PrepareReleaseType.Hotfix) {
            this.setState({
                ...this.state,
                cretateEditHotfixModalVisible: true,
            });
        } else if (e.key === PrepareReleaseType.Standard) {
            this.setState({
                ...this.state,
                createEditReleaseModalVisible: true,
            });
        }
    };

    handleOnClickLoadMoreReleases = () => {
        const limit = this.props.releasesPaginationLimit ?? 10;
        const offset = this.props.releasePaginationOffset ?? 0;

        this.props.fetchReleases(
            {
                orgId: this.props.orgId,
                appId: this.props.appId,
                orgIdParam: this.props.orgIdParam,
                appIdParam: this.props.appIdParam,
            },
            limit,
            offset,
        );
    };

    menu = (
        <Menu onClick={this.handleMenuClick}>
            <Menu.Item key={PrepareReleaseType.Standard}>Release</Menu.Item>
            <Menu.Item key={PrepareReleaseType.Hotfix}>Hotfix</Menu.Item>
        </Menu>
    );

    releaseGroups = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <ReleaseGroup
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    groupPhase={ReleaseTimelinePhase.Upcoming}
                    orgIdParam={this.props.orgIdParam}
                    appIdParam={this.props.appIdParam}
                />
                <ReleaseGroup
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    groupPhase={ReleaseTimelinePhase.Current}
                    orgIdParam={this.props.orgIdParam}
                    appIdParam={this.props.appIdParam}
                />
                <ReleaseGroup
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    groupPhase={ReleaseTimelinePhase.Completed}
                    orgIdParam={this.props.orgIdParam}
                    appIdParam={this.props.appIdParam}
                    onClickLoadMore={this.handleOnClickLoadMoreReleases}
                    totalCount={this.props.completedReleaseTotalCount}
                />
            </div>
        );
    };

    renderModals() {
        if (this.props.isSSRMigrated) {
            return (
                <>
                    <PrepareNewWorkflowRunModal
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        open={this.state.createEditReleaseModalVisible}
                        mode="standard"
                        onCancel={() => {
                            this.setState({
                                ...this.state,
                                createEditReleaseModalVisible: false,
                            });
                        }}
                        onSuccess={() => {
                            this.setState({
                                ...this.state,
                                createEditReleaseModalVisible: false,
                            });
                        }}
                    />
                    <PrepareNewWorkflowRunModal
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        open={this.state.cretateEditHotfixModalVisible}
                        mode="hotfix"
                        isConvertingRollback={this.props.lastCompletedReleaseHasActiveRollback}
                        onCancel={() => {
                            this.setState({
                                ...this.state,
                                cretateEditHotfixModalVisible: false,
                            });
                        }}
                        onSuccess={() => {
                            this.setState({
                                ...this.state,
                                cretateEditHotfixModalVisible: false,
                            });
                        }}
                    />
                </>
            );
        }

        return (
            <>
                <CreateEditReleaseModal
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    open={this.state.createEditReleaseModalVisible}
                    onCancel={() => {
                        this.setState({
                            ...this.state,
                            createEditReleaseModalVisible: false,
                        });
                    }}
                    onDidCreateOrEditRelease={() => {
                        this.setState({
                            ...this.state,
                            createEditReleaseModalVisible: false,
                        });
                    }}
                    isEditingRelease={false}
                    displayOnlyTargetDates={false}
                    canBeSubmitted={null}
                />
                <PrepareHotfixOrRollbackReleaseModal
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    open={this.state.cretateEditHotfixModalVisible}
                    isConvertingRollback={this.props.lastCompletedReleaseHasActiveRollback}
                    onCancel={() => {
                        this.setState({
                            ...this.state,
                            cretateEditHotfixModalVisible: false,
                        });
                    }}
                />
            </>
        );
    }

    render() {
        return (
            <div>
                {this.renderModals()}
                <div style={{ ...styles.timelineContent, paddingTop: 16, paddingBottom: 16 }}>
                    <Dropdown overlay={this.menu} trigger={['click']}>
                        <Button type="link" style={{ padding: 0 }}>
                            <PlusOutlined />
                            {this.props.isCollapsed ? null : 'Prepare new'}
                            {!this.props.isCollapsed && <DownOutlined />}
                        </Button>
                    </Dropdown>
                </div>
                <div>{this.releaseGroups()}</div>
            </div>
        );
    }
}

interface ReleasesProps extends AppComponentProps {
    isCollapsed: boolean;
}

const mapProps = (state: RootState, ownProps: ReleasesProps) => {
    const { orgId, appId } = ownProps;

    const org = selectOrg(state, orgId);
    const app = selectApp(state, appId);
    const completedReleases = selectCompletedReleases(state, appId);
    const completedReleaseTotalCount = app.completedReleaseTotalCount;
    const isDemoModeEnabled = selectIsAppsDemoModeEnabled(state);

    const paginatedReleasesPageLimitAndOffset = selectPaginatedReleasesLimitAndOffset(state, appId);

    const isCollapsed = ownProps.isCollapsed ?? false;

    const releasesPaginationLimit = paginatedReleasesPageLimitAndOffset.limit;
    const releasePaginationOffset = paginatedReleasesPageLimitAndOffset.offset;
    const shouldFetchCompletedReleases =
        isNil(releasePaginationOffset) && completedReleaseTotalCount !== 0 && !isDemoModeEnabled;

    const lastCompletedReleaseHasActiveRollback =
        completedReleases.length > 0 ? completedReleases[0].rollbackInfo?.status === ReleaseStatus.Active : false;

    const isSSRMigrated = selectIsWorkflowsSSRStepsEnabled(state, appId);

    return {
        appId,
        orgId,
        orgIdParam: org.orgId,
        appIdParam: app.appId,
        isCollapsed,
        releasesPaginationLimit,
        releasePaginationOffset,
        shouldFetchCompletedReleases,
        completedReleaseTotalCount,
        lastCompletedReleaseHasActiveRollback,
        isSSRMigrated,
    };
};

const mapDispatch = (dispatch) => {
    return {
        createNewRelease: (payload: NewReleasePayloadType) => {
            dispatch(tryNewReleaseAction(payload));
        },
        fetchReleases: (request: ReleasesRequest, limit: number, offset: number) => {
            dispatch(
                tryFetchPaginatedEntities(FetchablePaginatedEntityType.Releases, request, limit, offset, request.appId),
            );
        },
    };
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(Releases);
